import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import routesPath from 'routes/RoutesPath';
import { ArchiveIcon, BackIcon, DeleteIcon, DuplicateIcon, EditIconBlue, UnarchiveIcon } from 'assets/icons';
import AssignableMembersModal from 'containers/jobs/create-edit-job/components/AssignableMembersModal';
import CustomAvatar from 'components/avatar/CustomAvatar';
import { getInitials } from 'utils/commonUtils';
import CustomButton, { ButtonType } from 'components/custom-button/CustomButton';
import { useLazyGetAssignableMembersQuery } from 'containers/jobs/api';
import {
  useCreateCommissionRuleMutation,
  useDeleteCommissionRuleMutation,
  useLazyGetCommissionRulesQuery,
  useUpdateCommissionRuleMembersMutation,
  useUpdateCommissionRuleStatusMutation
} from './api';
import {
  CommissionRuleConditionIf,
  CommissionRuleConditionIfDisplay,
  CommissionRuleConditionOp,
  CommissionRuleConditionOpDisplay,
  CommissionRuleStatusDisplay,
  CommissionStatus,
  CommissionType
} from './constants';
import { CommissionRule, CreateCommissionRulePayload } from './commissionSettings.model';
import { useAppSelector } from 'hooks';
import { selectCurrentTeam, selectCurrentTeamAccountingPermissions } from 'containers/app/appSlice';
import { AssignableMember } from 'containers/jobs/jobs.model';
import Loader from 'components/loader/Loader';
import CreateRuleTypeModal from '../components/CreateRuleTypeModal';
import PayRuleCreationSuccessModal from '../components/PayRuleCreationSuccessModal';
import GenericDeleteModal from 'components/generic-delete-modal/GenericDeleteModal';
import { TooltipWrapper } from 'components/tooltip/TooltipWrapper';
import { InfoMesssages } from 'types/infoMessages';
import { NoPermissions } from 'components';
import { SettingsCleverTapEvents } from '../events';
import { logAnalyticEvent } from 'utils/analytics';
import CommissionRulesFilter, { CommissionRulesFilterValue } from './components/CommissionRulesFilter';
import { getCommissionBaseLabel } from '../utils';

const CommissionSettings = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const currentTeam = useAppSelector(selectCurrentTeam);
  const currentTeamAccountingPermissions = useAppSelector(selectCurrentTeamAccountingPermissions);
  const { editAccountingSettings, viewAccountingSettings } = currentTeamAccountingPermissions ?? {};

  const [showAssignMembersModal, setShowAssignMembersModal] = useState(false);
  const [selectedRuleForMembers, setSelectedRuleForMembers] = useState<CommissionRule | null>(null);
  const [getCommissionRules, { data: getCommissionRulesState, isFetching: isLoadingCommissionRules }] =
    useLazyGetCommissionRulesQuery();
  const [createCommissionRule, { isLoading: isCreating }] = useCreateCommissionRuleMutation();
  const [updateCommissionRuleStatus] = useUpdateCommissionRuleStatusMutation();
  const [deleteCommissionRule, { isLoading: isDeleting }] = useDeleteCommissionRuleMutation();
  const [rules, setRules] = useState<CommissionRule[]>([]);
  const [expandedConditions, setExpandedConditions] = useState<number[]>([]);
  const [getAssignableTeamMemberQuery, { data: assignableTeamMembers }] = useLazyGetAssignableMembersQuery();
  const [showRuleTypeModal, setShowRuleTypeModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState({
    isOpen: false,
    isEdit: false
  });
  const [updateCommissionRuleMembers] = useUpdateCommissionRuleMembersMutation();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedRuleForDelete, setSelectedRuleForDelete] = useState<CommissionRule | null>(null);
  const [filter, setFilter] = useState<CommissionRulesFilterValue>({
    status: [],
    assignedTo: [],
    condition: [],
    base: []
  });

  useEffect(() => {
    if (currentTeam?.id) {
      getCommissionRules({ teamId: currentTeam.id.toString() });
      getAssignableTeamMemberQuery(currentTeam.id.toString());
    }
  }, [currentTeam?.id]);

  useEffect(() => {
    if (currentTeam?.id) {
      const { status, assignedTo, condition, base } = filter;
      getCommissionRules({
        teamId: currentTeam.id.toString(),
        filter: {
          status: status?.length > 0 ? status.join(',') : null,
          assignedTo: assignedTo?.length > 0 ? assignedTo.join(',') : null,
          condition: condition?.length > 0 ? condition.join(',') : null,
          base: base?.length > 0 ? base.join(',')?.toLowerCase() : null
        }
      });
    }
  }, [JSON.stringify(filter)]);

  useEffect(() => {
    if (getCommissionRulesState) {
      setRules(getCommissionRulesState.data);
    }
  }, [getCommissionRulesState]);

  useEffect(() => {
    // Check if we're coming from create/edit with success state
    if (location.state?.showSuccessModal) {
      setShowSuccessModal({
        isOpen: true,
        isEdit: location.state.isEdit
      });
      // Clean up the location state
      navigate(location.pathname, { replace: true });
    }
  }, [location]);

  const handleAddMembers = (rule: CommissionRule) => {
    setSelectedRuleForMembers(rule);
    setShowAssignMembersModal(true);
  };

  const handleSaveMembers = async (selectedMembers: AssignableMember[]) => {
    if (!currentTeam?.id) return;

    try {
      await updateCommissionRuleMembers({
        teamId: currentTeam.id.toString(),
        commissionRuleId: selectedRuleForMembers.id,
        merchantIDs: selectedMembers?.map(member => member.merchantId) || []
      }).unwrap();

      // Refresh the rules list after updating members
      getCommissionRules({ teamId: currentTeam.id.toString() });
    } catch (error) {
      // Error handling is managed by RTK Query
    } finally {
      setShowAssignMembersModal(false);
      setSelectedRuleForMembers(null);
    }
  };

  const toggleExpandConditions = (ruleIndex: number) => {
    setExpandedConditions(
      prev => (prev.includes(ruleIndex) ? prev.filter(idx => idx !== ruleIndex) : [...prev, ruleIndex])
      // eslint-disable-next-line function-paren-newline
    );
  };

  const handleStatusChange = async (rule: CommissionRule) => {
    if (!currentTeam?.id) return;

    try {
      const newStatus = rule.status === CommissionStatus.ACTIVE ? CommissionStatus.ARCHIVED : CommissionStatus.ACTIVE;

      await updateCommissionRuleStatus({
        teamId: currentTeam.id.toString(),
        commissionRuleId: rule.id,
        status: newStatus
      }).unwrap();

      // Refresh the rules list after status update
      getCommissionRules({ teamId: currentTeam.id.toString() });
    } catch (error) {
      //error
    }
  };

  const handleDeleteRule = (rule: CommissionRule) => {
    setSelectedRuleForDelete(rule);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!currentTeam?.id || !selectedRuleForDelete) return;

    try {
      await deleteCommissionRule({
        teamId: currentTeam.id.toString(),
        commissionRuleId: selectedRuleForDelete.id
      }).unwrap();

      // Refresh the rules list after deletion
      getCommissionRules({ teamId: currentTeam.id.toString() });
      setShowDeleteModal(false);
      setSelectedRuleForDelete(null);
    } catch (error) {
      // Handle error
    }
  };

  const handleDuplicateRule = async (rule: CommissionRule) => {
    if (!currentTeam?.id) return;

    try {
      const duplicateRulePayload = {
        teamId: currentTeam.id.toString(),
        title: `${rule.title} (copy)`,
        conditions: rule.conditions,
        commissionType: rule.commissionType,
        commissionValue: rule.commissionValue,
        commissionApplyOn: rule.commissionApplyOn,
        memberIDs: rule.memberIds.map(member => member.id),
        status: CommissionStatus.ACTIVE
      };

      await createCommissionRule(duplicateRulePayload as CreateCommissionRulePayload).unwrap();

      // Refresh the rules list after successful duplication
      getCommissionRules({ teamId: currentTeam.id.toString() });
      logAnalyticEvent(SettingsCleverTapEvents.webSettingsCommissionDuplicateRuleClick);
    } catch (error) {
      // Error handling is managed by RTK Query
    }
  };

  const handleRuleTypeSelect = (ruleType: string) => {
    let navigationState = {};

    switch (ruleType) {
      case 'external_company':
        navigationState = {
          initialCondition: {
            conditionIf: CommissionRuleConditionIf.EXTERNAL_COMPANY_ID,
            conditionOp: CommissionRuleConditionOp.IS_ANY_OF,
            conditionValue: []
          }
        };
        break;
      case 'lead_source':
        navigationState = {
          initialCondition: {
            conditionIf: CommissionRuleConditionIf.LEAD_SOURCE,
            conditionOp: CommissionRuleConditionOp.IS_ANY_OF,
            conditionValue: []
          }
        };
        break;
      // default and custom cases don't need special handling
    }

    navigate(routesPath.CREATE_COMMISSION_RULE, { state: navigationState });
  };

  const handleBackClick = () => {
    navigate(routesPath.SETTINGS, { replace: true });
  };

  const handleCloseSuccessModal = () => {
    setShowSuccessModal({ isOpen: false, isEdit: false });
  };

  return (
    <div className="h-full w-full py-7 px-10">
      {/* Pay Rules Section */}
      <div className="mb-4 flex justify-between">
        <div>
          <div className="flex">
            <BackIcon
              className="path path-stroke-current mr-5 mt-2 h-5 w-5 cursor-pointer text-primaryText"
              onClick={handleBackClick}
            />
            <div className="flex flex-col gap-2">
              <div className="text-2xl font-bold text-primaryText">Commission rules</div>
              <div className="text-17px font-normal text-accentText">
                Create custom pay rules for flexible commission calculations
              </div>
            </div>
          </div>
        </div>
        <CustomButton
          id="create-new-rule"
          type={ButtonType.PRIMARY}
          onClick={() => setShowRuleTypeModal(true)}
          showDisabledReason={!editAccountingSettings}
          disabledReason={InfoMesssages.noPermissionToCreatePayRules}>
          + Create new rule
        </CustomButton>
      </div>
      <CommissionRulesFilter filter={filter} setFilter={setFilter} />
      {/* Table Section */}
      {viewAccountingSettings && getCommissionRulesState && !isLoadingCommissionRules && !isCreating ? (
        <div className="mt-6 w-full">
          <div className="overflow-hidden rounded-lg border border-borderGray">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="w-[11%] px-6 py-3 text-left text-sbase font-semibold text-primaryText">Rule name</th>
                  <th className="w-[18%] px-6 py-3 text-left text-sbase font-semibold text-primaryText">Condition</th>
                  <th className="w-[6%] px-6 py-3 text-left text-sbase font-semibold text-primaryText">Rate</th>
                  <th className="w-[10%] px-6 py-3 text-left text-sbase font-semibold text-primaryText">Base</th>
                  <th className="w-[6%] px-6 py-3 text-left text-sbase font-semibold text-primaryText">Status</th>
                  <th className="w-[11%] px-6 py-3 text-left text-sbase font-semibold text-primaryText">Members</th>
                  {editAccountingSettings && (
                    <th className="w-[13%] px-6 py-3 text-left text-sbase font-semibold text-primaryText">Actions</th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {rules?.length > 0 ? (
                  rules?.map((rule, index) => (
                    <tr key={index}>
                      <td className="px-6 py-3 align-top text-sbase font-semibold text-primaryText">{rule.title}</td>
                      <td className="px-6 py-3 align-top text-sbase font-normal text-primaryText">
                        <div className="flex flex-col items-start gap-1">
                          {rule.conditions.length > 0 ? (
                            <div key={rule.conditions[0].conditionIf}>
                              if {CommissionRuleConditionIfDisplay?.[rule.conditions[0].conditionIf]}{' '}
                              {CommissionRuleConditionOpDisplay?.[rule.conditions[0].conditionOp]}{' '}
                              <span className="text-accentText">{rule.conditions[0].conditionValue.join(', ')}</span>
                            </div>
                          ) : (
                            '-'
                          )}

                          {rule.conditions.length > 1 && (
                            <>
                              {expandedConditions.includes(index) ? (
                                // Show all conditions when expanded
                                rule.conditions.slice(1).map(condition => (
                                  <div key={condition.conditionIf}>
                                    and if {CommissionRuleConditionIfDisplay?.[condition.conditionIf]}{' '}
                                    {CommissionRuleConditionOpDisplay?.[condition.conditionOp]}{' '}
                                    <span className="text-accentText">{condition.conditionValue.join(', ')}</span>
                                  </div>
                                ))
                              ) : (
                                // Show +more link when collapsed
                                <div
                                  className="cursor-pointer text-secondary"
                                  onClick={() => toggleExpandConditions(index)}>
                                  +{rule.conditions.length - 1} more
                                </div>
                              )}

                              {expandedConditions.includes(index) && (
                                <div
                                  className="cursor-pointer text-secondary"
                                  onClick={() => toggleExpandConditions(index)}>
                                  Show less
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-3 align-top text-sbase text-primaryText">
                        {rule.commissionType === CommissionType.ABSOLUTE && '$'}
                        {rule.commissionValue}
                        {rule.commissionType === CommissionType.PERCENTAGE && '%'}
                      </td>
                      <td className="px-6 py-3 align-top text-sbase text-primaryText">
                        {getCommissionBaseLabel(rule.commissionApplyOn) ?? '-'}
                      </td>
                      <td className="px-6 py-3 align-top">
                        <span
                          className={`text-sbase ${
                            rule.status === CommissionStatus.ACTIVE ? ' text-successHighlight' : ' text-accentText'
                          }`}>
                          {CommissionRuleStatusDisplay?.[rule.status]}
                        </span>
                      </td>
                      <td className="px-6 py-3 align-top">
                        <div className="flex flex-col items-start gap-2">
                          <div className="flex items-center">
                            <div className="flex -space-x-1">
                              {rule.memberIds?.slice(0, 3).map(member => (
                                <CustomAvatar
                                  key={member.id}
                                  src={member.profilePictureURL}
                                  label={getInitials(member.name)}
                                  className="text-sm"
                                  width="w-5"
                                  height="h-5"
                                />
                              ))}
                            </div>
                            {rule.memberIds?.length > 3 && (
                              <span className="ml-2 text-primaryText">+{rule.memberIds?.length - 3}</span>
                            )}
                            {rule.memberIds?.length === 0 && <span className="text-primaryText">-</span>}
                          </div>
                          {editAccountingSettings && (
                            <div
                              className="cursor-pointer text-sbase font-semibold text-secondary"
                              onClick={() => handleAddMembers(rule)}>
                              +Add members
                            </div>
                          )}
                        </div>
                      </td>
                      {editAccountingSettings && (
                        <td className="px-6 py-3 align-top">
                          <div className="flex items-center gap-2">
                            <TooltipWrapper tooltip="Edit rule">
                              <button
                                className="rounded-md p-1 text-secondary hover:bg-gray-100"
                                onClick={() => navigate(`${routesPath.EDIT_COMMISSION_RULE_BASE}/${rule.id}`)}>
                                <EditIconBlue className="h-4 w-4" />
                              </button>
                            </TooltipWrapper>

                            <TooltipWrapper tooltip="Duplicate rule">
                              <button
                                className="rounded-md p-1 text-secondary hover:bg-gray-100"
                                onClick={() => handleDuplicateRule(rule)}>
                                <DuplicateIcon className="h-5 w-5" />
                              </button>
                            </TooltipWrapper>

                            <TooltipWrapper
                              tooltip={rule.status === CommissionStatus.ACTIVE ? 'Archive rule' : 'Activate rule'}>
                              <button
                                className="rounded-md p-1 text-secondary hover:bg-gray-100"
                                onClick={() => handleStatusChange(rule)}>
                                {rule.status === CommissionStatus.ACTIVE ? (
                                  <ArchiveIcon className="h-5 w-5" />
                                ) : (
                                  <UnarchiveIcon className="h-5 w-5" />
                                )}
                              </button>
                            </TooltipWrapper>

                            <TooltipWrapper tooltip="Delete rule">
                              <button
                                className="rounded-md p-1 text-secondary hover:bg-gray-100"
                                onClick={() => handleDeleteRule(rule)}>
                                <DeleteIcon className="h-4 w-4" />
                              </button>
                            </TooltipWrapper>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={7} className="px-6 py-3 text-center text-sbase text-accentText">
                      No rules found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="h-20"></div> {/* Spacer div */}
        </div>
      ) : (
        <div className="mt-6 flex h-[400px] items-center justify-center">
          {isLoadingCommissionRules && <Loader />}
          {!isLoadingCommissionRules && !viewAccountingSettings && <NoPermissions />}
        </div>
      )}

      {showAssignMembersModal && (
        <AssignableMembersModal
          list={assignableTeamMembers?.data?.teamMemberList || []}
          selectedMembers={
            (selectedRuleForMembers?.memberIds?.map(member => ({
              merchantId: member.id,
              name: member.name,
              profilePictureUrl: member.profilePictureURL,
              email: '',
              phone: ''
            })) as AssignableMember[]) || []
          }
          onClose={() => setShowAssignMembersModal(false)}
          onSave={handleSaveMembers}
        />
      )}

      {showRuleTypeModal && (
        <CreateRuleTypeModal
          isOpen={showRuleTypeModal}
          onClose={() => setShowRuleTypeModal(false)}
          onSelect={handleRuleTypeSelect}
        />
      )}

      {showSuccessModal.isOpen && (
        <PayRuleCreationSuccessModal
          open={showSuccessModal.isOpen}
          onClose={handleCloseSuccessModal}
          isEdit={showSuccessModal.isEdit}
        />
      )}

      {showDeleteModal && (
        <GenericDeleteModal
          showModal={showDeleteModal}
          isActionInProgress={isDeleting}
          title="Delete Commission Rule"
          subTitle={`Are you sure you want to delete "${selectedRuleForDelete?.title}"? This action cannot be undone.`}
          primaryBtnLabel="Delete"
          primaryBtnId="delete-commission-rule"
          onPrimaryBtnClick={handleConfirmDelete}
          secondaryBtnLabel="Cancel"
          secondaryBtnId="cancel-delete-commission-rule"
          onSecondaryBtnClick={() => {
            setShowDeleteModal(false);
            setSelectedRuleForDelete(null);
          }}
        />
      )}
    </div>
  );
};

export default CommissionSettings;
